import { useState, useRef, useEffect, useCallback } from 'react'
import { useTheme } from '../hooks/useTheme'
import { audioEffects } from '../utils/audioEffects'
import { getColorThemesWithLocks, getEasterEggDescription, type UnlockedColorTheme } from '../utils/easterEggUnlocks'
import { gsap } from 'gsap'
import './Settings.css'

interface SettingsProps {
  isOpen: boolean
  onClose: () => void
  buttonRef: React.RefObject<HTMLButtonElement | null>
  onShowHint?: (hint: string) => void
}

export const Settings: React.FC<SettingsProps> = ({ isOpen, onClose, buttonRef, onShowHint }) => {
  const { theme, setTheme, resetToSystemTheme, isSystemTheme } = useTheme()
  const [isResetSuccess, setIsResetSuccess] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const [currentColorTheme, setCurrentColorTheme] = useState<UnlockedColorTheme | null>(null)
  const [colorThemes, setColorThemes] = useState<UnlockedColorTheme[]>([])
  const settingsRef = useRef<HTMLDivElement>(null)
  const resetButtonRef = useRef<HTMLButtonElement>(null)
  const particlesRef = useRef<HTMLDivElement>(null)

  const createParticles = useCallback(() => {
    if (!resetButtonRef.current || !particlesRef.current) return

    const particleContainer = particlesRef.current
    const particleCount = 8

    // Clear existing particles
    particleContainer.innerHTML = ''

    // Create particles
    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div')
      particle.className = 'settings-particle'
      particleContainer.appendChild(particle)

      // Position at button center
      gsap.set(particle, {
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: 4,
        height: 4,
        borderRadius: '50%',
        backgroundColor: '#4caf50',
        pointerEvents: 'none',
        zIndex: 1000
      })

      // Calculate random direction
      const angle = (i / particleCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.5
      const distance = 30 + Math.random() * 20
      const endX = Math.cos(angle) * distance
      const endY = Math.sin(angle) * distance

      // Animate particle
      gsap.timeline()
        .to(particle, {
          x: endX,
          y: endY,
          scale: Math.random() * 0.5 + 0.5,
          opacity: 1,
          duration: 0.3,
          ease: 'power2.out'
        })
        .to(particle, {
          opacity: 0,
          scale: 0,
          duration: 0.3,
          ease: 'power2.in',
          onComplete: () => {
            if (particle.parentNode) {
              particle.parentNode.removeChild(particle)
            }
          }
        })
    }
  }, [])

  const hexToRgb = useCallback((hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null
  }, [])

  const applyColorTheme = useCallback((colorTheme: UnlockedColorTheme) => {
    const primaryRgb = hexToRgb(colorTheme.primary)
    const secondaryRgb = hexToRgb(colorTheme.secondary)
    
    if (primaryRgb && secondaryRgb) {
      const root = document.documentElement
      
      // Set primary accent color
      root.style.setProperty('--accent-color', colorTheme.primary)
      
      // Set secondary accent color
      root.style.setProperty('--accent-secondary', colorTheme.secondary)
      
      // Set gradient
      root.style.setProperty('--accent-gradient', `linear-gradient(to bottom right, ${colorTheme.primary}, ${colorTheme.secondary})`)
      
      // Set translucent variants
      root.style.setProperty('--accent-translucent', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.2)`)
      root.style.setProperty('--accent-secondary-translucent', `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.2)`)
      
      // Set hover and interaction variants
      root.style.setProperty('--accent-hover-background', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1)`)
      root.style.setProperty('--accent-shadow', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.2)`)
      
      // Set hero backgrounds with accent color hints
      // Dark theme: very dark with slight accent tint
      const darkHeroBg = `rgb(${Math.max(5, Math.floor(primaryRgb.r * 0.06))}, ${Math.max(5, Math.floor(primaryRgb.g * 0.08))}, ${Math.max(15, Math.floor(primaryRgb.b * 0.12))})`
      // Light theme: very light with subtle accent warmth
      const lightHeroBg = `rgb(${Math.min(255, 250 + Math.floor(primaryRgb.r * 0.02))}, ${Math.min(255, 248 + Math.floor(primaryRgb.g * 0.015))}, ${Math.min(255, 245 + Math.floor(primaryRgb.b * 0.01))})`
      
      root.style.setProperty('--hero-bg-dark', darkHeroBg)
      root.style.setProperty('--hero-bg-light', lightHeroBg)
      
      // Save to localStorage
      localStorage.setItem('color-theme', JSON.stringify(colorTheme))
      
      // Update current color theme state
      setCurrentColorTheme(colorTheme)
    }
  }, [hexToRgb])

  const handleReset = useCallback(() => {
    // Create particle effect
    createParticles()
    
    // Play success sound immediately
    audioEffects.playSuccessChime()
    
    // Default theme and color values
    const defaultColorTheme = { name: 'Default', primary: '#ff6b35', secondary: '#f7931e' }
    
    // Reset to system theme preference
    resetToSystemTheme()
    
    // Reset colors
    applyColorTheme(defaultColorTheme)
    
    // Clear color theme localStorage (theme is already cleared by resetToSystemTheme)
    localStorage.removeItem('color-theme')
    
    // Show success state immediately
    setIsResetSuccess(true)
    setTimeout(() => {
      setIsResetSuccess(false)
    }, 1000)
  }, [resetToSystemTheme, applyColorTheme, createParticles])

    // Load saved color theme on mount
  useEffect(() => {
    const savedColorTheme = localStorage.getItem('color-theme')
    if (savedColorTheme) {
      try {
        const colorTheme = JSON.parse(savedColorTheme)
        setCurrentColorTheme(colorTheme)
        // Only apply if colors aren't already applied by the initializer
        const currentAccentColor = getComputedStyle(document.documentElement).getPropertyValue('--accent-color').trim()
        if (!currentAccentColor || currentAccentColor !== colorTheme.primary) {
          applyColorTheme(colorTheme)
        }
      } catch (error) {
        console.warn('Failed to parse saved color theme:', error)
        // Apply default theme if parsing fails
        const defaultColorTheme = { name: 'Default', primary: '#ff6b35', secondary: '#f7931e' }
        setCurrentColorTheme(defaultColorTheme)
        applyColorTheme(defaultColorTheme)
      }
    } else {
      // Set default color theme
      const defaultColorTheme = { name: 'Default', primary: '#ff6b35', secondary: '#f7931e' }
      setCurrentColorTheme(defaultColorTheme)
    }
  }, [applyColorTheme])

  // Load color themes and listen for easter egg unlocks
  useEffect(() => {
    const updateColorThemes = () => {
      setColorThemes(getColorThemesWithLocks())
    }

    // Initial load
    updateColorThemes()

    // Listen for easter egg unlocks
    const handleEasterEggUnlock = () => {
      updateColorThemes()
    }

    window.addEventListener('easterEggUnlocked', handleEasterEggUnlock)
    window.addEventListener('easterEggReset', handleEasterEggUnlock)

    return () => {
      window.removeEventListener('easterEggUnlocked', handleEasterEggUnlock)
      window.removeEventListener('easterEggReset', handleEasterEggUnlock)
    }
  }, [])

  useEffect(() => {
    if (isOpen && buttonRef.current && settingsRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect()
      const settingsRect = settingsRef.current.getBoundingClientRect()
      
      let top = buttonRect.bottom + 8
      let left = buttonRect.left
      
      // Adjust if popup would go off screen
      if (left + settingsRect.width > window.innerWidth) {
        left = buttonRect.right - settingsRect.width
      }
      
      if (top + settingsRect.height > window.innerHeight) {
        top = buttonRect.top - settingsRect.height - 8
      }
      
      setPosition({ top, left })
    }
  }, [isOpen, buttonRef])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        settingsRef.current &&
        !settingsRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose, buttonRef])

  if (!isOpen) return null

  return (
    <div
      ref={settingsRef}
      className="settings-popup"
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 1000
      }}
    >
      <div className="settings-section">
        <h3>Kies je thema</h3>
        <div className="theme-toggle-section">
          <button
            className={`theme-option ${!isSystemTheme && theme === 'light' ? 'active' : ''}`}
            onClick={() => {
              if (theme !== 'light' || isSystemTheme) {
                audioEffects.playSwitchClick()
                setTheme('light')
              }
            }}
            onMouseEnter={() => audioEffects.playHover()}
          >
            <div className="theme-preview light-preview"></div>
            Licht
          </button>
          <button
            className={`theme-option ${!isSystemTheme && theme === 'dark' ? 'active' : ''}`}
            onClick={() => {
              if (theme !== 'dark' || isSystemTheme) {
                audioEffects.playSwitchClick()
                setTheme('dark')
              }
            }}
            onMouseEnter={() => audioEffects.playHover()}
          >
            <div className="theme-preview dark-preview"></div>
            Donker
          </button>
          <button
            className={`theme-option ${isSystemTheme ? 'active' : ''}`}
            onClick={() => {
              if (!isSystemTheme) {
                audioEffects.playSwitchClick()
                resetToSystemTheme()
              }
            }}
            onMouseEnter={() => audioEffects.playHover()}
          >
            <div className="theme-preview system-preview"></div>
            Systeem
          </button>
        </div>
      </div>

      <div className="settings-section">
        <h3>Kies je eigen kleurtje</h3>
        <div className="color-grid">
          {colorThemes.map((colorTheme) => {
            const isSelected = currentColorTheme && 
              currentColorTheme.primary === colorTheme.primary && 
              currentColorTheme.secondary === colorTheme.secondary
            const isLocked = colorTheme.isLocked
            
            return (
              <button
                key={colorTheme.name}
                className={`color-swatch ${isSelected ? 'selected' : ''} ${isLocked ? 'locked' : ''}`}
                style={{
                  background: isLocked 
                    ? 'linear-gradient(135deg, #666 0%, #444 100%)'
                    : `linear-gradient(135deg, ${colorTheme.primary} 0%, ${colorTheme.secondary} 100%)`
                }}
                onClick={() => {
                  if (isLocked) {
                    audioEffects.playHintChime()
                    // Show hint if onShowHint is available and the color has a required easter egg
                    if (onShowHint && colorTheme.requiredEasterEgg) {
                      const hint = getEasterEggDescription(colorTheme.requiredEasterEgg)
                      onShowHint(`Hint: ${hint}`)
                    } else {
                      console.log('onShowHint not available or no required easter egg')
                    }
                    return
                  }
                  audioEffects.playBlop()
                  applyColorTheme(colorTheme)
                }}
                onMouseEnter={() => {
                  if (!isLocked) {
                    audioEffects.playHover()
                  }
                }}
                title={isLocked ? `🔒 ${colorTheme.name} - Unlock by discovering secrets!` : colorTheme.name}
              >
                {isSelected && !isLocked && (
                  <div className="color-swatch-indicator">
                    <div className="color-swatch-checkmark">✓</div>
                  </div>
                )}
                {isLocked && (
                  <div className="color-swatch-lock">
                    <div className="color-swatch-lock-icon">🔒</div>
                  </div>
                )}
              </button>
            )
          })}
        </div>
      </div>

      <div className="settings-section">
        <div style={{ position: 'relative', width: '100%' }}>
          <button 
            ref={resetButtonRef}
            className={`reset-button ${isResetSuccess ? 'success' : ''}`}
            onClick={handleReset}
            disabled={isResetSuccess}
          >
            <span className="reset-icon">
              {isResetSuccess ? '✓' : '↻'}
            </span>
            {isResetSuccess ? 'Gereset!' : 'Reset kleurtjes'}
          </button>
          <div 
            ref={particlesRef}
            className="settings-particle-container"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              overflow: 'visible'
            }}
          />
        </div>
      </div>
    </div>
  )
}
